// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import { formatISODate, formatTime } from "@clipboard-health/date-time";
import {
  BottomSheet,
  CbhIcon,
  Divider,
  Text,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { format } from "date-fns";
import { useState } from "react";

import { type HiringEvent } from "../../PlacementCandidate/api/useGetHiringEvent";
import { type InterviewAvailability } from "../api/useGetInterviewAvailability";
import { type PlacementDetail } from "../api/useGetPlacement";
import { useRecruiterCallTimeStorage } from "../hooks/useRecruiterCallTimeStorage";
import { hasOnlyOpenHouseAvailable } from "../utils";
import { AlternativeAddressModal } from "./AlternativeAddressModal";
import { InterviewDateSelector } from "./InterviewDateSelector";
import { InterviewTimeSelector } from "./InterviewTimeSelector";

const DATE_FORMAT = "EEE, MMM d";

function getInterviewButtonLabel(params: {
  isReschedule: boolean;
  isProspectWorkplace: boolean;
  isPhoneScreen: boolean;
}): string {
  const { isReschedule, isProspectWorkplace, isPhoneScreen } = params;

  if (isProspectWorkplace) {
    return isReschedule ? "Reschedule walk-in" : "Book yourself as a walk-in";
  }

  if (isPhoneScreen) {
    return isReschedule ? "Reschedule phone call" : "Book a pre-interview phone call";
  }

  return isReschedule ? "Reschedule interview" : "Book an in-person interview";
}

interface PlacementDetailBookInterviewDialogProps {
  placementDetails: PlacementDetail;
  openHouseWithDetails?: HiringEvent;
  isReschedule: boolean;
  modalState: UseModalState;
  interviewAvailability: InterviewAvailability;
  onBookInterview: (time: { start: Date; end: Date }) => void;
  interviewType?: "in-person-interview" | "phone-screen";
  placementId: string;
  bookingReorderingCohort: "book_first" | "screen_first" | "screen_last";
}

// eslint-disable-next-line complexity
export function PlacementDetailBookInterviewDialog(props: PlacementDetailBookInterviewDialogProps) {
  const {
    placementDetails,
    openHouseWithDetails,
    isReschedule,
    modalState,
    interviewAvailability,
    onBookInterview,
    interviewType,
    placementId,
    bookingReorderingCohort,
  } = props;

  const hiringEventAlternativeAddressModalState = useModalState();
  const { loadRecruiterCallTime, clearRecruiterCallTime } = useRecruiterCallTimeStorage();

  // Build a dictionary: "YYYY-MM-DD" => [{ start: Date, end: Date }, ...]
  const availabilityMap = interviewAvailability.reduce<
    Record<
      string,
      Array<{ start: Date; end: Date; eventType: "open-house" | "interview-days" | undefined }>
    >
  >((accumulator, slot) => {
    const startDate = format(slot.start, DATE_FORMAT);
    accumulator[startDate] ||= [];

    accumulator[startDate].push({
      start: slot.start,
      end: slot.end,
      eventType: slot.eventType,
    });
    return accumulator;
  }, {});

  const availableDates = Object.keys(availabilityMap);
  const isProspectWorkplace = Boolean(placementDetails.workplace.placementSettings?.isProspect);

  const eventContext = {
    placementId: placementDetails.id,
    placementApplicationId: placementDetails.applications?.[0]?.id,
    totalTimeSlots: interviewAvailability.length,
    totalAvailableDates: availableDates.length,
    isReschedule,
    isProspectWorkplace,
    ...(isDefined(bookingReorderingCohort) && { bookingReorderingCohort }),
  };

  // Our currently selected date and time

  // select index in open house availability, else 0

  const selectedDateIndex = availableDates.findIndex((date) =>
    availabilityMap[date].some((slot) => slot.eventType === "open-house")
  );

  // Restore interview time from localStorage for screen_last cohort users who haven't passed screening
  const shouldRestoreStoredTime = placementId && bookingReorderingCohort === "screen_last";

  const storedInterviewTime = shouldRestoreStoredTime
    ? loadRecruiterCallTime(placementId)
    : undefined;

  let restoredTimeData: { date: string; time: { start: Date; end: Date } } | undefined;

  if (isDefined(storedInterviewTime)) {
    const matchingSlot = interviewAvailability.find(
      (slot) =>
        slot.start.getTime() === storedInterviewTime.start.getTime() &&
        slot.end.getTime() === storedInterviewTime.end.getTime()
    );
    if (isDefined(matchingSlot)) {
      restoredTimeData = {
        date: format(matchingSlot.start, DATE_FORMAT),
        time: matchingSlot,
      };
    } else {
      clearRecruiterCallTime(placementId);
      restoredTimeData = undefined;
    }
  }

  const [selectedDate, setSelectedDate] = useState<string | undefined>(() => {
    if (restoredTimeData) {
      return restoredTimeData.date;
    }
    return selectedDateIndex === -1 ? availableDates[0] : availableDates[selectedDateIndex];
  });

  const [selectedTime, setSelectedTime] = useState<{ start: Date; end: Date } | undefined>(() => {
    if (restoredTimeData) {
      return restoredTimeData.time;
    }
    return undefined;
  });

  const isSelectionPromoted =
    selectedDateIndex !== -1 && selectedDate === availableDates[selectedDateIndex];

  const promotedDate = selectedDateIndex === -1 ? undefined : availableDates[selectedDateIndex];

  const timeSlots =
    selectedDate && isDefined(availabilityMap[selectedDate]) ? availabilityMap[selectedDate] : [];

  // Track if we should scroll to the selected time (only when restored from localStorage)
  const shouldScrollToSelected = isDefined(restoredTimeData);

  /**
   * Handle when a user clicks on a day in the calendar UI.
   */
  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setSelectedTime(undefined);
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_DATE_SELECTED, {
      ...eventContext,
      selectedDate: date,
      selectedDateTimeSlots: availabilityMap[date].length,
    });
  };

  const shouldDisableDate = (dateKey: string) => {
    if (!isDefined(availabilityMap[dateKey])) {
      return true;
    }

    return availabilityMap[dateKey].length === 0;
  };

  const isInterviewSlotAvailable = interviewAvailability.length > 0;

  // Use the actual interview type if provided (for rescheduling), otherwise fall back to workplace settings
  const isPhoneScreen =
    interviewType === "phone-screen" ||
    (!interviewType &&
      placementDetails.interviewSettings?.attributes.selfServeInterviewBookingType ===
        "phone-screen");

  const buttonText = getInterviewButtonLabel({
    isReschedule,
    isProspectWorkplace,
    isPhoneScreen,
  });

  const handleBookInterview = (selectedDate: string, selectedTime: { start: Date; end: Date }) => {
    logEvent(APP_V2_APP_EVENTS.INTERVIEW_BOOKING_SUBMITTED, {
      ...eventContext,
      selectedDate: selectedTime.start ? formatISODate(selectedTime.start) : undefined,
      selectedDateDisplay: selectedDate,
      selectedTimeRange: selectedTime
        ? `${formatTime(selectedTime.start)} - ${formatTime(selectedTime.end)}`
        : undefined,
      promotedDate,
      hasSelectedPromotedDate: isDefined(promotedDate)
        ? isDefined(selectedDate) && selectedDate === promotedDate
        : undefined,
    });

    onBookInterview(selectedTime);
  };

  const handleClose = () => {
    modalState.closeModal();
  };

  return (
    <>
      <BottomSheet
        modalState={modalState}
        header={<BottomSheetHeader onClose={handleClose} />}
        footer={
          <DialogFooter orientation="vertical">
            {isInterviewSlotAvailable && (
              <>
                <Text variant="body2" align="center">
                  {isPhoneScreen
                    ? "By continuing, you agree that we are authorized to contact you for phone call attendance verification via email, SMS, or phone call."
                    : "By continuing, you agree that we are authorized to contact you for the interview attendance confirmation via email, sms or a phone call."}
                </Text>
                <LoadingButton
                  fullWidth
                  variant="contained"
                  size="large"
                  isLoading={false}
                  disabled={!selectedDate || !selectedTime}
                  onClick={async () => {
                    if (!selectedDate || !selectedTime) {
                      return;
                    }

                    const isOpenHouseDate = availabilityMap[selectedDate].some(
                      (slot) => slot.eventType === "open-house"
                    );
                    const isAlternativeAddress =
                      placementDetails.address.formatted !==
                      openHouseWithDetails?.address.formatted;

                    if (
                      isDefined(openHouseWithDetails) &&
                      isOpenHouseDate &&
                      isAlternativeAddress &&
                      !hasOnlyOpenHouseAvailable(interviewAvailability)
                    ) {
                      hiringEventAlternativeAddressModalState.openModal();
                    } else {
                      handleBookInterview(selectedDate, selectedTime);
                    }
                  }}
                >
                  {buttonText}
                </LoadingButton>
              </>
            )}
          </DialogFooter>
        }
        onClose={handleClose}
      >
        <Box sx={{ px: 5, py: 6 }}>
          {isInterviewSlotAvailable ? (
            <>
              <Text variant="h4" align="left" sx={{ fontWeight: 500, pb: 4 }}>
                {isPhoneScreen
                  ? "Choose your call time"
                  : `Choose your ${isProspectWorkplace ? "walk-in" : "interview"} time`}
              </Text>
              <InterviewDateSelector
                promotedDate={promotedDate}
                availableDates={availableDates}
                selectedDate={selectedDate}
                shouldDisableDate={shouldDisableDate}
                onDateChange={handleDateChange}
              />
              <Divider />

              <InterviewTimeSelector
                isSelectionPromoted={isSelectionPromoted}
                placementDetails={placementDetails}
                timeSlots={timeSlots}
                selectedTime={selectedTime}
                isReschedule={isReschedule}
                shouldScrollToSelected={shouldScrollToSelected}
                onTimeSelect={setSelectedTime}
              />
            </>
          ) : (
            <Stack
              spacing={4}
              sx={{ p: 2, height: "300px" }}
              alignItems="center"
              justifyContent="center"
            >
              <CbhIcon type="info" color={(theme) => theme.palette.warning.main} size="large" />
              <Text semibold variant="h5" align="center">
                No {isProspectWorkplace ? "walk-in" : "interview"} slots are available at this time.
                Please check back later.
              </Text>
              <Text variant="body2" align="center">
                Please note that interview dates after your documents expire won&apos;t be visible.
              </Text>
            </Stack>
          )}
        </Box>
      </BottomSheet>
      {hiringEventAlternativeAddressModalState.modalIsOpen && isDefined(openHouseWithDetails) && (
        <AlternativeAddressModal
          modalState={hiringEventAlternativeAddressModalState}
          placementDetails={placementDetails}
          alternativeAddressData={{
            coordinates: {
              longitude: openHouseWithDetails.coordinates.longitude,
              latitude: openHouseWithDetails.coordinates.latitude,
            },
            formattedAddress: openHouseWithDetails.address.formatted,
            distance: openHouseWithDetails.distance ?? 0,
            description: openHouseWithDetails.addressDescription ?? "",
          }}
          onContinue={() => {
            if (!selectedDate || !selectedTime) {
              return;
            }

            handleBookInterview(selectedDate, selectedTime);
          }}
        />
      )}
    </>
  );
}
